import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  withStyles,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Typography,
  Paper,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
// @material-ui/icons
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import LaunchIcon from '@material-ui/icons/Launch';

import styles from './styles';

export const getBillingReason = (billingReason) => {
  switch (billingReason) {
    case 'subscription_update':
      return 'Subscription Change';
    case 'subscription_cycle':
      return 'Billing Period End';
    case 'subscription_create':
      return 'New Subscription';
    default:
      return 'Unknown';
  }
};

export class InvoicesCardComponent extends React.Component {
  render() {
    const {
      classes,
      upcomingInvoice,
      invoices,
      isFetching,
    } = this.props;
    return (
      <div>
        <Paper className={classes.paperContainer} elevation={4}>
          <Typography variant="h5" component="h3">
            Upcoming Invoice
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice Period Start</TableCell>
                <TableCell>Invoice Period End</TableCell>
                <TableCell>Current Expected Amount Due</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow key="upcoming-invoice">
                <TableCell>
                  {upcomingInvoice
                      && new Date(upcomingInvoice.period_start * 1000).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {upcomingInvoice
                    && new Date(upcomingInvoice.period_end * 1000).toLocaleDateString()}
                </TableCell>
                <TableCell>{upcomingInvoice && (upcomingInvoice.amount_due / 100).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
                </TableCell>
                <TableCell>
                  <div>{(upcomingInvoice && upcomingInvoice.lines && upcomingInvoice.lines.data)
                    ? upcomingInvoice.lines.data.map((line) => line.description).join(', ')
                    : 'No upcoming invoice'}
                  </div>
                  {
                        (upcomingInvoice
                            && upcomingInvoice.discount && upcomingInvoice.discount.coupon)
                            && (
                              <Chip
                                size="small"
                                label={upcomingInvoice.discount.coupon.percent_off
                                  ? (`${upcomingInvoice.discount.coupon.percent_off}% off with ${upcomingInvoice.discount.coupon.name} discount`)
                                  : (`$${upcomingInvoice.discount.coupon.percent_off} off with ${upcomingInvoice.discount.coupon.name} discount`)}
                              />
                            )
                      }
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
        <Card>
          <CardHeader
            title="Past Invoices"
          />
          <CardContent className={classes.cardBody}>
            <Typography variant="body1" gutterBottom>
              Invoices are triggered whenever you upgrade or change your subscription.
              Your payment method on file will be charged automatically at the beginning of each
              billing cycle (monthly or annually) depending on when you started your subscription
              and for your chosen billing frequency. A new invoice will be generated after any
              charge.
            </Typography>
            {
                isFetching
                && (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                )
            }
            {
                !isFetching
                && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice Period</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Amount Due</TableCell>
                        <TableCell>Amount Paid</TableCell>
                        <TableCell>Invoice Reason</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Invoice</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.sort((a, b) => b.date - a.date).map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            <div>{new Date(invoice.periodStart * 1000).toLocaleDateString()}</div>
                            <div>to</div>
                            <div>{new Date(invoice.periodEnd * 1000).toLocaleDateString()}</div>
                          </TableCell>
                          <TableCell>{(invoice.total / 100).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                          </TableCell>
                          <TableCell>{(invoice.amountDue / 100).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                          </TableCell>
                          <TableCell>{(invoice.amountPaid / 100).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                          </TableCell>
                          <TableCell>
                            {getBillingReason(invoice.billingReason)}
                          </TableCell>
                          <TableCell>
                            <div>
                              {
                              invoice.lines.data.map((line) => line.description).join(', ')
                            }
                            </div>
                            {
                            (invoice.discount && invoice.discount.coupon)
                              && (
                                <Chip
                                  size="small"
                                  label={invoice.discount.coupon.percentOff
                                    ? (`${invoice.discount.coupon.percentOff}% off with ${invoice.discount.coupon.name} discount`)
                                    : (`$${invoice.discount.coupon.percentOff} off with ${invoice.discount.coupon.name} discount`)}
                                />
                              )
                          }
                          </TableCell>
                          <TableCell>{invoice.paid
                            ? <Chip label="Paid" className={classes.chipPaid} color="primary" />
                            : <Chip label="Pending or Awaiting Payment" className={classes.chipNotPaid} />}
                          </TableCell>
                          <TableCell>
                            {
                            invoice.paid
                              ? (
                                <div>
                                  <Tooltip title="Download Invoice">
                                    <IconButton aria-label="Download Invoice" href={invoice.invoicePdf}>
                                      <CloudDownloadIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Go To Hosted Invoice">
                                    <IconButton aria-label="Go To Invoice" href={invoice.hostedInvoiceUrl} target="_blank">
                                      <LaunchIcon />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              )
                              : (
                                <div>
                                  {
                                  invoice.hostedInvoiceUrl
                                  && (
                                    <Tooltip title="Pay Invoice">
                                      <Button variant="extendedFab" className={classes.errorButton} aria-label="Go To Invoice" href={invoice.hostedInvoiceUrl} target="_blank">
                                        <LaunchIcon className={classes.buttonIcon} />
                                        Pay Here
                                      </Button>
                                    </Tooltip>
                                  )
                                }
                                </div>
                              )
                          }

                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )

            }
          </CardContent>
        </Card>
      </div>
    );
  }
}

InvoicesCardComponent.propTypes = {
  classes: PropTypes.object,
  isFetching: PropTypes.bool,
  invoices: PropTypes.array,
  upcomingInvoice: PropTypes.object,
};

InvoicesCardComponent.defaultProps = {
  classes: {},
  isFetching: true,
  invoices: [],
  upcomingInvoice: null,
};

export const mapStateToProps = (state) => ({ isFetching: state.tenant.isLoadingStripeInvoices });

export default connect(
  mapStateToProps,
  null,
)(withStyles(styles, { withTheme: true })(InvoicesCardComponent));
